import type { AutomationStatsType } from "@/types/graphql/partner.generated";
import {
  formatMoney,
  formatPercentValue,
} from "@/partner/shared/utils/formatLocale";

/**
 * Счётчики, из которых считаются доли. Строка списка не запрашивает
 * отписки, поэтому поле необязательное: без него доля отписок null.
 */
export type BroadcastRatesInput = Pick<
  AutomationStatsType,
  "sent" | "delivered" | "opened" | "clicked" | "purchases" | "revenueUSD"
> &
  Partial<Pick<AutomationStatsType, "unsubscribed">>;

export interface BroadcastRates {
  delivery: number | null;
  open: number | null;
  click: number | null;
  clickToOpen: number | null;
  unsubscribe: number | null;
  conversion: number | null;
  revenuePerEmail: number | null;
}

// Процент с одним знаком; не больше 100: Apple Mail открывает письма сам,
// и открытий бывает больше, чем доставок по вебхуку.
const ratio = (a: number, b: number): number | null =>
  b > 0 ? Math.min(100, Math.round((a / b) * 1000) / 10) : null;

/**
 * Доли из спеки («Эффективность»): null значит «нет данных» (например,
 * вебхуки доставки не подключены или письма ещё не доставлены).
 */
export function broadcastRates(stats: BroadcastRatesInput): BroadcastRates {
  const base = stats.delivered > 0 ? stats.delivered : null;
  return {
    delivery: ratio(stats.delivered, stats.sent),
    open: base ? ratio(stats.opened, base) : null,
    click: base ? ratio(stats.clicked, base) : null,
    clickToOpen: ratio(stats.clicked, stats.opened),
    unsubscribe:
      base && typeof stats.unsubscribed === "number"
        ? ratio(stats.unsubscribed, base)
        : null,
    conversion: base ? ratio(stats.purchases, base) : null,
    revenuePerEmail: base
      ? Math.round((stats.revenueUSD / base) * 100) / 100
      : null,
  };
}

/**
 * Доля для таблицы и карточки по языку интерфейса: «12,3 %» (ru),
 * «12.3%» (en). value уже в процентах (как у broadcastRates).
 */
export const formatPercent = (value: number, locale: string): string =>
  formatPercentValue(value, locale);

/** Сумма в долларах по языку интерфейса: «1 234,50 $» (ru), «$1,234.50» (en). */
export const formatUsd = (value: number, locale: string): string =>
  formatMoney(value, "USD", locale);
